import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Share2, MapPin, Calendar, Crosshair, Trophy, Activity, CheckCircle2, XCircle, Clock, AlertCircle, Link2, Loader2 } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

// Faceit skill level colors
const FACEIT_LEVEL_COLORS = {
  1: "#ccc", 2: "#7bc473", 3: "#7bc473", 4: "#f7cd39",
  5: "#f7cd39", 6: "#f77f39", 7: "#f77f39", 8: "#f23439",
  9: "#f23439", 10: "#d40000"
};

export default function FaceitProfile() {
  const { username } = useParams();
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [linkInput, setLinkInput] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkSuccess, setLinkSuccess] = useState("");
  const [copyToast, setCopyToast] = useState(false);

  useEffect(() => {
    const setMetaTag = (attrName, attrValue, content) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
      element.classList.add("dynamic-meta-tag");
    };

    const removeMetaTags = () => {
      document.title = "INDUNGI";
      document.querySelectorAll(".dynamic-meta-tag").forEach(t => t.remove());
    };

    setLoading(true);

    // Fetch user profile and CS2 stats in parallel
    Promise.all([
      api.get(`/users/${username}`),
      api.get(`/stats/${username}/cs2`).catch(() => ({ data: { linked: false } }))
    ])
      .then(([userRes, statsRes]) => {
        const u = userRes.data.user;
        setUserData(userRes.data);
        setStats(statsRes.data);
        setLoading(false);

        document.title = `${u.username} - eSports Profile | INDUNGI`;
        setMetaTag("property", "og:title", `${u.username} - INDUNGI eSports Profile`);
        setMetaTag("property", "og:description", `View ${u.username}'s CS2 stats, Faceit ELO, match history, and performance on INDUNGI.`);
        setMetaTag("property", "og:type", "profile");
        setMetaTag("property", "og:url", window.location.href);
        if (u.avatar_url) {
          setMetaTag("property", "og:image", u.avatar_url);
          setMetaTag("name", "twitter:image", u.avatar_url);
        }
        setMetaTag("name", "twitter:card", "summary_large_image");
        setMetaTag("name", "twitter:title", `${u.username} - INDUNGI eSports Profile`);
        setMetaTag("name", "twitter:description", `View ${u.username}'s CS2 Faceit stats on INDUNGI.`);
      })
      .catch(() => {
        setUserData({ error: true });
        setLoading(false);
      });

    return () => { removeMetaTags(); };
  }, [username]);

  const handleLinkFaceit = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    setLinkError("");
    setLinkSuccess("");
    try {
      await api.post("/stats/link-faceit", { faceit_nickname: linkInput });
      setLinkSuccess("Contul Faceit a fost sincronizat! Reîncarcă pagina pentru a vedea statisticile.");
      // Re-fetch stats
      setStatsLoading(true);
      const res = await api.get(`/stats/${username}/cs2`);
      setStats(res.data);
      setStatsLoading(false);
    } catch (err) {
      setLinkError(err?.response?.data?.detail || "Eroare la sincronizare. Verifică nickname-ul și încearcă din nou.");
    } finally {
      setLinkLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090b0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (userData?.error) {
    return (
      <div className="min-h-screen bg-[#090b0f] flex items-center justify-center text-zinc-500 font-mono">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
          <div className="text-lg font-bold text-white">Player not found</div>
          <Link to="/members" className="mt-4 inline-block text-amber-400 hover:underline text-sm">Browse Members</Link>
        </div>
      </div>
    );
  }

  const u = userData.user;
  const bannerImg = u.banner_url || "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=2070&auto=format&fit=crop";
  const isVip = u.is_vip || (u.vip_tier && u.vip_tier !== "none");
  const faceitLinked = stats?.linked === true;
  const levelColor = FACEIT_LEVEL_COLORS[stats?.skill_level] || "#ccc";
  const matches = stats?.recent_matches || [];

  // Check if viewing own profile
  const meStr = localStorage.getItem("idg_user");
  let isMe = false;
  try { isMe = meStr && JSON.parse(meStr).username === u.username; } catch {}

  return (
    <div className="min-h-screen bg-[#090b0f] text-zinc-200 font-sans selection:bg-amber-500/30">
      
      {/* Clipboard toast */}
      {copyToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] bg-zinc-800 text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-xl border border-white/10 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Link copiat!
        </div>
      )}

      {/* Sticky Nav */}
      <div className="sticky top-0 z-50 bg-[#0f1115]/90 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <Link to="/forums" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-semibold uppercase tracking-wider">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-center gap-3">
          {faceitLinked && (
            <a
              href={`https://www.faceit.com/en/players/${stats.faceit_nickname}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#FF5500]/10 hover:bg-[#FF5500]/20 text-[#FF5500] rounded border border-[#FF5500]/20 text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" /> Faceit Profile
            </a>
          )}
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded border border-white/10 text-sm font-bold uppercase tracking-wider transition-colors"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative">
        <div
          className="h-[280px] w-full bg-cover bg-center relative"
          style={{ backgroundImage: `url(${bannerImg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#090b0f] via-[#090b0f]/50 to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative -mt-20">
          <div className="flex flex-col md:flex-row items-end gap-6 mb-8">
            {/* Avatar */}
            <div className="relative group flex-shrink-0">
              <div className="w-36 h-36 rounded-xl overflow-hidden border-4 border-[#090b0f] bg-[#1a1d24] shadow-2xl">
                <img
                  src={u.avatar_url || `https://ui-avatars.com/api/?name=${u.username}&background=1a1d24&color=fff&size=256`}
                  alt={u.username}
                  className="w-full h-full object-cover"
                />
              </div>
              {isVip && (
                <div className="absolute -bottom-3 -right-3 w-9 h-9 bg-amber-500 rounded-lg flex items-center justify-center text-[#090b0f] shadow-[0_0_15px_rgba(245,158,11,0.5)] z-10" title="VIP">
                  <Trophy className="w-5 h-5" />
                </div>
              )}
              {/* Faceit Level Badge */}
              {faceitLinked && stats?.skill_level && (
                <div
                  className="absolute -top-3 -right-3 w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm border-2 z-10"
                  style={{ backgroundColor: levelColor + "22", borderColor: levelColor, color: levelColor }}
                  title={`Faceit Level ${stats.skill_level}`}
                >
                  {stats.skill_level}
                </div>
              )}
            </div>

            {/* Username & Meta */}
            <div className="flex-1 pb-1 min-w-0">
              <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight flex items-center gap-3 flex-wrap">
                <span className="truncate">{u.username}</span>
                {isVip && <span className="text-sm px-3 py-1 bg-amber-500/20 text-amber-400 border border-amber-500/40 rounded uppercase tracking-widest font-bold">VIP</span>}
                {faceitLinked && (
                  <span className="text-sm px-3 py-1 bg-[#FF5500]/20 text-[#FF5500] border border-[#FF5500]/30 rounded uppercase tracking-widest font-bold">Faceit</span>
                )}
              </h1>
              <div className="mt-3 flex items-center gap-4 text-xs text-zinc-400 font-medium flex-wrap">
                {u.location && (
                  <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-500" /> {u.location}</div>
                )}
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-zinc-500" /> Joined {u.joined_at ? format(new Date(u.joined_at), "MMM yyyy") : "2026"}
                </div>
                {u.steam_id && (
                  <div className="flex items-center gap-1.5 text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded border border-cyan-400/20">
                    Steam Linked
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="pb-1 flex gap-3 w-full md:w-auto">
              <button className="flex-1 md:flex-none px-8 py-3 bg-white text-black font-black uppercase tracking-wider rounded hover:bg-zinc-200 transition-colors shadow-lg text-sm">
                Follow
              </button>
              <button className="flex-1 md:flex-none px-6 py-3 bg-[#1a1d24] text-white font-black uppercase tracking-wider rounded border border-white/10 hover:bg-[#252830] transition-colors text-sm">
                Message
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="space-y-6">

            {/* CS2 / Faceit Stats Box */}
            <div className="bg-[#13161c] rounded-xl border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5 bg-[#171a21]/50 flex items-center gap-2">
                <Crosshair className="w-5 h-5 text-amber-500" />
                <h3 className="font-bold text-white uppercase tracking-wider text-sm">CS2 Statistics</h3>
                {faceitLinked && <span className="ml-auto text-[10px] px-2 py-0.5 bg-[#FF5500]/20 text-[#FF5500] rounded font-bold uppercase tracking-wider">Live Faceit</span>}
              </div>

              {statsLoading ? (
                <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
              ) : faceitLinked ? (
                <div className="p-6">
                  {/* ELO Display */}
                  <div className="text-center mb-6">
                    <div className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-1">FACEIT ELO</div>
                    <div className="text-5xl font-black" style={{ color: levelColor }}>{stats.elo}</div>
                    <div className="mt-2 flex items-center justify-center gap-1.5">
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center font-black text-xs"
                        style={{ backgroundColor: levelColor + "22", color: levelColor }}
                      >
                        {stats.skill_level}
                      </div>
                      <span className="text-zinc-400 text-sm font-bold">Level {stats.skill_level}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: "Matches", value: stats.matches_played },
                      { label: "Win Rate", value: stats.win_rate },
                      { label: "Avg K/D", value: stats.avg_kd },
                      { label: "Headshots", value: stats.avg_headshots },
                    ].map(s => (
                      <div key={s.label} className="bg-[#1a1d24] p-3 rounded text-center">
                        <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">{s.label}</div>
                        <div className="text-xl font-bold text-white">{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Not linked state
                <div className="p-6">
                  <div className="text-center mb-5">
                    <AlertCircle className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
                    <div className="text-zinc-400 text-sm font-medium leading-relaxed">
                      {isMe
                        ? "Sincronizează-ți contul Faceit pentru a afișa statisticile CS2 reale."
                        : "Acest jucător nu a sincronizat un cont Faceit încă."}
                    </div>
                  </div>

                  {isMe && (
                    <form onSubmit={handleLinkFaceit} className="space-y-3">
                      <input
                        type="text"
                        value={linkInput}
                        onChange={e => setLinkInput(e.target.value)}
                        placeholder="Nickname Faceit (ex: BL1NG)"
                        className="w-full bg-[#1a1d24] border border-white/10 text-white text-sm px-4 py-2.5 rounded focus:outline-none focus:border-[#FF5500]/60 placeholder:text-zinc-600"
                        required
                      />
                      {linkError && <div className="text-red-400 text-xs font-medium">{linkError}</div>}
                      {linkSuccess && <div className="text-emerald-400 text-xs font-medium">{linkSuccess}</div>}
                      <button
                        type="submit"
                        disabled={linkLoading}
                        className="w-full py-2.5 bg-[#FF5500] text-white font-black uppercase tracking-wider rounded hover:bg-[#e04e00] transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {linkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        Sincronizează Faceit
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Community Stats */}
            <div className="bg-[#13161c] rounded-xl border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5 bg-[#171a21]/50">
                <h3 className="font-bold text-white uppercase tracking-wider text-sm">Community Details</h3>
              </div>
              <div className="p-6 space-y-4">
                {u.bio && (
                  <div>
                    <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">About</div>
                    <p className="text-zinc-300 text-sm leading-relaxed">{u.bio}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-y-4 pt-4 border-t border-white/5">
                  <div>
                    <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Forum Posts</div>
                    <div className="text-lg font-bold text-white">{u.message_count || 0}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Reactions</div>
                    <div className="text-lg font-bold text-white">{u.reaction_score || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Match History */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-white uppercase tracking-wider flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-500" /> Recent Matches
              </h2>
              {faceitLinked && (
                <a
                  href={`https://www.faceit.com/en/players/${stats.faceit_nickname}/stats/cs2`}
                  target="_blank" rel="noreferrer"
                  className="text-xs font-bold text-zinc-400 hover:text-[#FF5500] uppercase tracking-wider transition-colors"
                >
                  View on Faceit →
                </a>
              )}
            </div>

            <div className="bg-[#13161c] rounded-xl border border-white/5 overflow-hidden">
              {!faceitLinked ? (
                <div className="p-12 text-center text-zinc-600">
                  <Crosshair className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <div className="font-bold text-sm uppercase tracking-wider">
                    {isMe ? "Sincronizează Faceit pentru a vedea istoricul meciurilor" : "Niciun cont Faceit sincronizat"}
                  </div>
                </div>
              ) : matches.length === 0 ? (
                <div className="p-12 text-center text-zinc-600">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <div className="font-bold text-sm uppercase tracking-wider">Niciun meci recent găsit</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[560px]">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 bg-[#171a21]/50 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                      <div className="col-span-4">Result / Map</div>
                      <div className="col-span-3 text-center">Score</div>
                      <div className="col-span-2 text-center">K/D</div>
                      <div className="col-span-3 text-right">Date</div>
                    </div>
                    {/* Matches */}
                    <div className="divide-y divide-white/5">
                      {matches.map((match) => {
                        const isWin = match.result === "W";
                        let matchDate;
                        try { matchDate = new Date(match.date); } catch { matchDate = null; }
                        const kdFloat = parseFloat(match.kd);

                        return (
                          <div key={match.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-white/[0.02] transition-colors">
                            <div className="col-span-4 flex items-center gap-3">
                              <div className={`w-8 h-8 rounded flex items-center justify-center border shrink-0 ${
                                isWin
                                  ? "bg-emerald-500/10 border-emerald-500/20"
                                  : "bg-red-500/10 border-red-500/20"
                              }`}>
                                {isWin
                                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                  : <XCircle className="w-4 h-4 text-red-500" />
                                }
                              </div>
                              <div>
                                <div className={`font-bold text-sm ${isWin ? "text-emerald-400" : "text-red-400"}`}>
                                  {isWin ? "VICTORIE" : "ÎNFRÂNGERE"}
                                </div>
                                <div className="text-xs text-zinc-500">{match.map}</div>
                              </div>
                            </div>

                            <div className="col-span-3 text-center font-black text-lg text-white font-mono">{match.score}</div>

                            <div className="col-span-2 text-center">
                              {match.kd !== "N/A" ? (
                                <span className={`font-bold text-sm ${!isNaN(kdFloat) && kdFloat >= 1 ? "text-emerald-400" : "text-red-400"}`}>
                                  {match.kd}
                                </span>
                              ) : (
                                <span className="text-zinc-600 text-sm">—</span>
                              )}
                            </div>

                            <div className="col-span-3 text-right text-xs text-zinc-500 flex items-center justify-end gap-1">
                              <Clock className="w-3 h-3" />
                              {matchDate ? formatDistanceToNow(matchDate, { addSuffix: true }) : "—"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
